import React, { useMemo, useState } from 'react';
import { Modal, Table, Button, Form, Alert, Badge } from 'react-bootstrap';
import API from '../services/api';
import Pager from './Pager';
import { keyFor, isSimilar } from '../utils/expenseCategoryMatch';

const PAGE_SIZE = 10;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Gastos → "Categorías": search, add, rename, join or delete expense categories.
// - The box at the top searches as you type (accents/case ignored) and lists the possible matches — the names that
//   contain what was typed first, then the look-alikes (same rule the server uses to ask "¿parecida?"). From the same
//   box a new category is added: an exact match can't be added again, a look-alike needs "Agregar de todas formas".
// - A category that looks like another is marked "¿Repetida?"; "Unir" moves its gastos into the other and removes it,
//   which is how existing duplicates are cleaned up. "Sin categoría" (where a deleted category's gastos go) can receive
//   but can't be renamed, joined away or deleted.
const ExpenseCategoriesModal = ({ categories, onClose, onChanged }) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [onlyRepeated, setOnlyRepeated] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(null); // the category
  const [merging, setMerging] = useState(null); // { category, into }
  const [similar, setSimilar] = useState(null); // look-alikes of the name being renamed
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const keyed = useMemo(() => categories.map(c => ({ ...c, key: keyFor(c.name) })), [categories]);
  // For each category, the others it looks like (a probable duplicate).
  const lookAlikes = useMemo(() => {
    const map = new Map();
    keyed.forEach(a => map.set(a._id, keyed.filter(b => b._id !== a._id && isSimilar(a.key, b.key))));
    return map;
  }, [keyed]);
  const repeatedCount = keyed.filter(c => !c.protected && lookAlikes.get(c._id).length).length;

  const typed = query.trim();
  const typedKey = keyFor(typed);
  const exact = typedKey ? keyed.find(c => c.key === typedKey) : null;
  const contains = typedKey ? keyed.filter(c => c.key.includes(typedKey)) : [];
  const alike = typedKey ? keyed.filter(c => !c.key.includes(typedKey) && isSimilar(typedKey, c.key)) : [];
  const alikeIds = new Set(alike.map(c => c._id));

  let rows = typedKey ? [...contains, ...alike] : keyed;
  if (onlyRepeated) rows = rows.filter(c => !c.protected && lookAlikes.get(c._id).length);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const shown = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const reset = () => { setEditing(null); setDeleting(null); setMerging(null); setSimilar(null); setError(''); setNotice(''); };
  const search = (value) => { setQuery(value); setPage(1); setError(''); setNotice(''); };

  const add = async (confirmSimilar = false) => {
    if (!typedKey || exact) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const { data } = await API.post('/expense-categories', { name: typed, confirmSimilar });
      setNotice(`Categoría "${data.name}" agregada.`);
      setQuery('');
      onChanged('add');
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'DUPLICATE') setError(data.message || 'Esa categoría ya existe.');
      else if (data.code === 'SIMILAR') setError('Hay categorías parecidas: revisa la lista antes de agregarla.');
      else setError(data.message || 'No se pudo agregar la categoría.');
    } finally { setBusy(false); }
  };

  const rename = async (confirmSimilar = false) => {
    setBusy(true); setError('');
    try {
      const { data } = await API.put(`/expense-categories/${editing.id}`, { name: editing.name, confirmSimilar });
      setNotice(`"${data.previousName}" ahora se llama "${data.name}"${data.gastosUpdated ? ` (${plural(data.gastosUpdated, 'gasto')} actualizado${data.gastosUpdated === 1 ? '' : 's'})` : ''}.`);
      setEditing(null); setSimilar(null);
      onChanged();
    } catch (err) {
      if (err.response?.data?.code === 'SIMILAR') setSimilar(err.response.data.similar);
      else setError(err.response?.data?.message || 'No se pudo cambiar la categoría.');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await API.delete(`/expense-categories/${deleting._id}`);
      setNotice(`"${data.deleted}" se borró${data.gastosMoved ? `; ${plural(data.gastosMoved, 'gasto')} pasaron a "${data.movedTo}"` : ''}.`);
      setDeleting(null);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo borrar la categoría.');
    } finally { setBusy(false); }
  };

  const merge = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await API.post(`/expense-categories/${merging.category._id}/merge`, { into: merging.into });
      setNotice(`"${data.merged}" se unió con "${data.into}"${data.gastosMoved ? ` (${plural(data.gastosMoved, 'gasto')} movido${data.gastosMoved === 1 ? '' : 's'})` : ''}.`);
      setMerging(null);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudieron unir las categorías.');
    } finally { setBusy(false); }
  };

  // Merge targets: its look-alikes first, then everything else.
  const mergeTargets = (category) => {
    const near = lookAlikes.get(category._id) || [];
    const nearIds = new Set(near.map(c => c._id));
    return { near, rest: keyed.filter(c => c._id !== category._id && !nearIds.has(c._id)) };
  };
  const startMerge = (c) => {
    reset();
    const { near, rest } = mergeTargets(c);
    setMerging({ category: c, into: (near[0] || rest[0])?._id || '' });
  };

  const addHint = () => {
    if (!typedKey) return null;
    if (exact) return <span>Ya existe <strong>{exact.name}</strong>.</span>;
    if (alike.length || contains.length) {
      const list = [...contains, ...alike].slice(0, 4).map(c => c.name).join(', ');
      return <span className="text-warning">Posibles coincidencias: <strong>{list}</strong>{contains.length + alike.length > 4 ? '…' : ''}. Usa la existente si es lo mismo.</span>;
    }
    return <span>No hay ninguna parecida.</span>;
  };
  const needsConfirm = !exact && alike.length + contains.length > 0;

  return (
    <Modal show onHide={busy ? undefined : onClose} centered size="lg">
      <Modal.Header closeButton={!busy}><Modal.Title>Categorías de gastos</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form onSubmit={(e) => { e.preventDefault(); if (!needsConfirm) add(); }} className="mb-2">
          <div className="d-flex gap-2">
            <Form.Control
              autoFocus
              maxLength={60}
              value={query}
              placeholder="Buscar o agregar una categoría..."
              onChange={e => search(e.target.value)}
              disabled={busy}
            />
            {typedKey && !exact && (
              needsConfirm
                ? <Button variant="outline-warning" className="text-nowrap" disabled={busy} onClick={() => add(true)}><i className="bi bi-plus-lg me-1" />Agregar de todas formas</Button>
                : <Button type="submit" className="text-nowrap" disabled={busy}><i className="bi bi-plus-lg me-1" />Agregar “{typed}”</Button>
            )}
            {query && <Button variant="outline-secondary" disabled={busy} onClick={() => search('')} title="Limpiar"><i className="bi bi-x-lg" /></Button>}
          </div>
          {typedKey && <div className="small mt-1" style={{ color: 'var(--mm-muted)' }}>{addHint()}</div>}
        </Form>

        <div className="d-flex justify-content-between align-items-center mb-2 small" style={{ color: 'var(--mm-muted)' }}>
          <span>{typedKey ? `${plural(rows.length, 'resultado')}` : `${plural(categories.length, 'categoría')}`}</span>
          {(repeatedCount > 0 || onlyRepeated) && (
            <Form.Check
              type="switch"
              id="only-repeated"
              label={`Solo posibles repetidas (${repeatedCount})`}
              checked={onlyRepeated}
              onChange={e => { setOnlyRepeated(e.target.checked); setPage(1); }}
            />
          )}
        </div>

        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {notice && <Alert variant="info" className="py-2">{notice}</Alert>}
        {deleting && (
          <Alert variant="warning">
            ¿Borrar <strong>{deleting.name}</strong>?
            {deleting.count ? <> Sus {plural(deleting.count, 'gasto')} pasarán a <strong>Sin categoría</strong>.</> : ' No tiene gastos.'}
            <div className="mt-2 d-flex gap-2">
              <Button size="sm" variant="danger" disabled={busy} onClick={remove}>{busy ? 'Borrando...' : 'Sí, borrar'}</Button>
              <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setDeleting(null)}>Cancelar</Button>
            </div>
          </Alert>
        )}
        {merging && (() => {
          const { near, rest } = mergeTargets(merging.category);
          return (
            <Alert variant="warning">
              Unir <strong>{merging.category.name}</strong> con:
              <Form.Select size="sm" className="my-2" value={merging.into} disabled={busy} onChange={e => setMerging({ ...merging, into: e.target.value })}>
                {near.length > 0 && <optgroup label="Parecidas">{near.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}</optgroup>}
                <optgroup label={near.length ? 'Las demás' : 'Categorías'}>{rest.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}</optgroup>
              </Form.Select>
              <div className="small">
                {merging.category.count ? <>Sus {plural(merging.category.count, 'gasto')} pasarán a esa categoría y </> : 'No tiene gastos; '}
                <strong>{merging.category.name}</strong> desaparecerá de la lista.
              </div>
              <div className="mt-2 d-flex gap-2">
                <Button size="sm" variant="warning" disabled={busy || !merging.into} onClick={merge}>{busy ? 'Uniendo...' : 'Sí, unir'}</Button>
                <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setMerging(null)}>Cancelar</Button>
              </div>
            </Alert>
          );
        })()}
        {similar && (
          <Alert variant="warning">
            Se parece a: <strong>{similar.map(s => s.name).join(', ')}</strong>. ¿Usar "{editing?.name}" de todas formas?
            <div className="mt-2 d-flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => rename(true)}>Sí, guardar</Button>
              <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setSimilar(null)}>Volver</Button>
            </div>
          </Alert>
        )}

        <Table size="sm" hover className="mb-0 align-middle">
          <thead><tr><th>Categoría</th><th className="text-end">Gastos</th><th /></tr></thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={3} className="text-center py-3" style={{ color: 'var(--mm-muted)' }}>{typedKey ? 'Ninguna categoría coincide.' : 'Sin categorías.'}</td></tr>
            )}
            {shown.map(c => {
              const repeated = !c.protected && lookAlikes.get(c._id).length > 0;
              return (
                <tr key={c._id}>
                  <td>
                    {editing?.id === c._id ? (
                      <Form onSubmit={(e) => { e.preventDefault(); rename(); }} className="d-flex gap-2">
                        <Form.Control size="sm" autoFocus maxLength={60} value={editing.name} onChange={e => { setEditing({ ...editing, name: e.target.value }); setSimilar(null); }} />
                        <Button size="sm" type="submit" disabled={busy || !editing.name.trim()}>Guardar</Button>
                        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => { setEditing(null); setSimilar(null); }}>Cancelar</Button>
                      </Form>
                    ) : (
                      <>
                        {c.name}
                        {alikeIds.has(c._id) && <Badge bg="secondary" className="ms-2">parecida</Badge>}
                        {repeated && (
                          <Badge bg="warning" text="dark" className="ms-2" title={`Se parece a: ${lookAlikes.get(c._id).map(o => o.name).join(', ')}`}>¿Repetida?</Badge>
                        )}
                        {c.protected && <span className="text-muted small"> — aquí van los gastos de las categorías borradas</span>}
                      </>
                    )}
                  </td>
                  <td className="text-end">{c.count || 0}</td>
                  <td className="text-end text-nowrap">
                    {!c.protected && editing?.id !== c._id && (
                      <>
                        <Button size="sm" variant="link" className="p-0 me-3" title="Cambiar nombre" disabled={busy} onClick={() => { reset(); setEditing({ id: c._id, name: c.name }); }}><i className="bi bi-pencil" /></Button>
                        <Button size="sm" variant="link" className={`p-0 me-3${repeated ? ' text-warning' : ''}`} title="Unir con otra categoría" disabled={busy} onClick={() => startMerge(c)}><i className="bi bi-intersect" /></Button>
                        <Button size="sm" variant="link" className="p-0 text-danger" title="Borrar" disabled={busy} onClick={() => { reset(); setDeleting(c); }}><i className="bi bi-trash" /></Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {pages > 1 && <div className="d-flex justify-content-center mt-3"><Pager page={current} pages={pages} onChange={setPage} /></div>}
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cerrar</Button></Modal.Footer>
    </Modal>
  );
};

export default ExpenseCategoriesModal;
